/**
 * Single source of truth for the new Treatments mega-menu, the /treatments
 * index page, and every /concerns/:slug landing page. Keeping the taxonomy
 * here means a future menu item is one edit away from being everywhere.
 */

export const TREATMENT_CATEGORIES = [
  {
    id: "prevention",
    label: { en: "Prevention · Age Well", fr: "Prévention · Vieillir en Beauté" },
    landingHref: "/prevention",
    // Feb 2026 · Karine ask — clinic badges per treatment card. The
    // Prevention program is Karine's personal 1:1 timeline consult —
    // available at all three clinics on her rotation days.
    defaultClinics: ["ottawa", "kanata", "cp"],
    blurb: {
      en: "Prevention is the New Rejuvenation. Age-based programs for your 20s, 30s, 40s, 50s+ and Men — designed to preserve collagen and prevent aging before it starts.",
      fr: "La Prévention est le Nouveau Rajeunissement. Programmes par tranche d'âge pour préserver le collagène et prévenir le vieillissement.",
    },
    image: "/img/treatments/prevention.webp",
    items: [
      {
        name: { en: "The Age Well Timeline · 20s / 30s / 40s / 50s+ / Men", fr: "Chronologie Bien Vieillir · 20 / 30 / 40 / 50+ / Hommes" },
        href: "/prevention",
        featured: true,
        locationNote: { en: "Karine's Prevention Philosophy · NEW", fr: "Philosophie de Prévention de Karine · NOUVEAU" },
      },
    ],
  },
  // Oct 2 2026 · Karine ask ─────────────────────────────────────────
  // "Instead of injectable heading I want separate Botox, Fillers,
  //  Sculptra, Radiesse, Skin Boosters heading so they click and it
  //  immediately goes to that page not a generic page."
  //
  // Split the old "Injectables · Fillers · Skin Boosters & Bio" umbrella
  // into FIVE independent top-level categories. The heading of each is
  // a Link whose landingHref is the exact destination page — tapping
  // the heading goes straight there, no detour through /treatments/injectables.
  {
    id: "botox",
    label: { en: "Botox", fr: "Botox" },
    landingHref: "/botox-ottawa",
    defaultClinics: ["ottawa", "kanata", "cp"],
    blurb: {
      en: "Neuromodulators for lines, prevention, and Nefertiti lifts — Karine's signature touch.",
      fr: "Neuromodulateurs pour les rides, la prévention et les Nefertiti Lift — la signature de Karine.",
    },
    image: "/img/treatments/injectables.webp",
    items: [
      { name: { en: "Baby Botox · Prevention", fr: "Baby Botox · Prévention" }, href: "/botox-ottawa", featured: true },
      { name: { en: "Nefertiti Lift · Jawline + Neck", fr: "Nefertiti Lift · Mâchoire + Cou" }, href: "/botox-ottawa" },
      { name: { en: "Full Upper Face Package", fr: "Haut du Visage Complet" }, href: "/botox-ottawa" },
    ],
  },
  {
    id: "fillers",
    label: { en: "Fillers", fr: "Fillers" },
    landingHref: "/dermal-fillers",
    defaultClinics: ["ottawa", "kanata", "cp"],
    blurb: {
      en: "Hyaluronic acid dermal fillers for lips, cheeks, chin, jawline, and under-eye hollows.",
      fr: "Fillers d'acide hyaluronique pour lèvres, pommettes, menton, mâchoire et cernes.",
    },
    image: "/img/treatments/injectables.webp",
    items: [
      { name: { en: "Dermal Fillers · Face Sculpting", fr: "Fillers Dermiques · Sculpture du Visage" }, href: "/dermal-fillers", featured: true },
      { name: { en: "Lip Filler", fr: "Augmentation des Lèvres" }, href: "/lip-filler-ottawa" },
      { name: { en: "Combos · Botox + Filler", fr: "Combos · Botox + Filler" }, href: "/combos", featured: true },
    ],
  },
  {
    id: "sculptra",
    label: { en: "Sculptra", fr: "Sculptra" },
    landingHref: "/sculptra",
    defaultClinics: ["ottawa", "kanata", "cp"],
    blurb: {
      en: "Collagen-stimulating poly-L-lactic acid for a gradual, natural lift that lasts up to 2 years.",
      fr: "Acide poly-L-lactique stimulant le collagène pour un lift graduel et naturel.",
    },
    image: "/img/treatments/injectables.webp",
    items: [
      { name: { en: "Sculptra Bio-Stimulator · Full Face", fr: "Sculptra Bio-Stimulateur · Visage Complet" }, href: "/sculptra", featured: true },
    ],
  },
  {
    id: "radiesse",
    label: { en: "Radiesse", fr: "Radiesse" },
    landingHref: "/radiesse",
    defaultClinics: ["ottawa", "kanata", "cp"],
    blurb: {
      en: "CaHA biostimulator for face, hands, and neck — instant structure + ongoing collagen boost.",
      fr: "Biostimulateur CaHA pour visage, mains et cou — structure immédiate + collagène à long terme.",
    },
    image: "/img/treatments/injectables.webp",
    items: [
      { name: { en: "Radiesse® · Face · Hands · Neck", fr: "Radiesse® · Visage · Mains · Cou" }, href: "/radiesse", featured: true, locationNote: { en: "From $750 · face, hands, neck / dec", fr: "Dès 750 $ · visage, mains, cou / déc" } },
    ],
  },
  {
    id: "skin-boosters",
    label: { en: "Skin Boosters", fr: "Boosters" },
    landingHref: "/skin-boosters",
    defaultClinics: ["ottawa", "kanata", "cp"],
    blurb: {
      en: "Baby Glow, Profhilo, and PRP for a lit-from-within hydration that no cream can match.",
      fr: "Baby Glow, Profhilo et PRP pour une hydratation lumineuse qu'aucune crème n'égale.",
    },
    image: "/img/treatments/injectables.webp",
    items: [
      { name: { en: "Skin Boosters & Baby Glow", fr: "Boosters & Baby Glow" }, href: "/skin-boosters", featured: true },
      { name: { en: "Microneedling + PRP", fr: "Microneedling + PRP" }, href: "/medical-microneedling" },
    ],
  },
  {
    id: "womens-health",
    label: { en: "Women's & Men's Health · GLP-1 · Menopause · Andropause", fr: "Santé Féminine & Masculine · GLP-1 · Ménopause · Andropause" },
    landingHref: "/womens-mens-health",
    // Rebuilding Programs run across all three clinics — each client
    // picks their home clinic for the 4-6 month journey.
    defaultClinics: ["ottawa", "kanata", "cp"],
    blurb: {
      en: "Karine's signature Rebuilding Programs — for women navigating GLP-1 weight loss or peri- & post-menopause, and for men navigating andropause. Restore collagen, structure, and confidence where hormones or rapid change quietly erode them.",
      fr: "Les Programmes de Reconstruction signature de Karine — pour les femmes en GLP-1 ou ménopause, et pour les hommes en andropause. Restaurer collagène, structure et confiance.",
    },
    image: "/img/treatments/womens-health.webp",
    items: [
      {
        name: { en: "GLP-1 Rebuilding · Lift & Restore Program™", fr: "Reconstruction GLP-1 · Programme Lift & Restore™" },
        href: "/lift-restore-program",
        featured: true,
        locationNote: { en: "4-6 month journey · from $3,999", fr: "Parcours 4-6 mois · dès 3 999 $" },
      },
      {
        name: { en: "Menopause Rebuilding Program™", fr: "Programme de Reconstruction Ménopause™" },
        href: "/menopause-program",
        featured: true,
        locationNote: { en: "5-6 month journey · from $1,499", fr: "Parcours 5-6 mois · dès 1 499 $" },
      },
      {
        name: { en: "The Andropause Restore Program™ · For Men", fr: "Programme Restauration Andropause™ · Pour Hommes" },
        href: "/andropause-program",
        featured: true,
        locationNote: { en: "6-month journey · from $1,499 · NEW", fr: "Parcours 6 mois · dès 1 499 $ · NOUVEAU" },
      },
    ],
  },
  {
    id: "laser-skin",
    label: { en: "Laser & Skin", fr: "Laser & Peau" },
    landingHref: "/laser-and-skin",
    // Laser + aesthetician-led services run OUT OF OTTAWA ONLY — the
    // Lumenis platform + aesthetician schedule are both anchored there.
    // Clients from Kanata/CP travel to Ottawa for these.
    defaultClinics: ["ottawa"],
    blurb: {
      en: "Hydrafacials, dermaplaning, RF microneedling, IPL, chemical peels — light-based and resurfacing care.",
      fr: "Hydrafacial, dermaplaning, RF microneedling, IPL, peelings — traitements lumière et resurfaçage.",
    },
    image: "/img/treatments/laser.webp",
    items: [
      // Feb 2026 · Karine ask — Laser Hair Removal must stand on its
      // own category, NOT nested here. See the dedicated `laser-hair`
      // category below.
      { name: { en: "Laser & HydraFacial", fr: "Laser & HydraFacial" }, href: "/laser-and-skin", featured: true, locationNote: { en: "Ottawa · with a Medical Aesthetician", fr: "Ottawa · avec une esthéticienne médicale" } },
      { name: { en: "RF Microneedling", fr: "RF Microneedling" }, href: "/laser-and-skin#rf-microneedling", featured: true, locationNote: { en: "Ottawa · with a Medical Aesthetician", fr: "Ottawa · avec une esthéticienne médicale" } },
      // Iter 264 · Tanya Shatenko public references scrubbed — mega-menu items
      // now read "Ottawa · with a Medical Aesthetician" so no single team
      // member is promoted anywhere on the public site.
      { name: { en: "Lash & Brow Studio", fr: "Studio Cils & Sourcils" }, href: "/lash-brow-studio", featured: true, locationNote: { en: "Ottawa · with a Medical Aesthetician", fr: "Ottawa · avec une esthéticienne médicale" } },
      { name: { en: "HydraFacial", fr: "HydraFacial" }, href: "/hydrafacial", locationNote: { en: "Ottawa · with a Medical Aesthetician", fr: "Ottawa · avec une esthéticienne médicale" } },
      { name: { en: "Laser Genesis", fr: "Laser Genesis" }, href: "/laser-and-skin#laser-genesis", locationNote: { en: "Ottawa · with a Medical Aesthetician", fr: "Ottawa · avec une esthéticienne médicale" } },
      { name: { en: "IPL Photofacial", fr: "IPL Photofacial" }, href: "/laser-and-skin#ipl-photofacial", locationNote: { en: "Ottawa · with a Medical Aesthetician", fr: "Ottawa · avec une esthéticienne médicale" } },
    ],
  },
  // Feb 2026 · Karine ask — "laser hair bends its own heading and its
  // own page and not on with any other treatments please." Promoted
  // Laser Hair Removal to a dedicated top-level category so its menu
  // pill is distinct and leads directly to the standalone green page.
  {
    id: "laser-hair",
    label: { en: "Laser Hair Removal", fr: "Épilation Laser" },
    landingHref: "/laser-hair-removal",
    defaultClinics: ["ottawa"],
    blurb: {
      en: "Medical-grade hair removal performed by our female Medical Aesthetician — single areas, Head-to-Toe Unlimited, House Package (Buy 6 → 6 small FREE).",
      fr: "Épilation laser médicale réalisée par notre esthéticienne médicale — zones individuelles, forfait Tête-aux-pieds Illimité, Promo Maison.",
    },
    image: "/img/treatments/laser-hair.webp",
    items: [
      { name: { en: "All Areas · Priced by Zone", fr: "Toutes les zones · Prix par zone" }, href: "/laser-hair-removal", featured: true, locationNote: { en: "Ottawa · with a Medical Aesthetician", fr: "Ottawa · avec une esthéticienne médicale" } },
      { name: { en: "The House Package · Buy 5 → 6th FREE", fr: "Promo Maison · Achetez 5 → la 6ᵉ GRATUITE" }, href: "/laser-hair-removal#the-house-package", featured: true, locationNote: { en: "Equal or lesser value · stacks any mix", fr: "Valeur égale ou inférieure · combinable toutes zones" } },
      { name: { en: "Head-to-Toe Unlimited · $249/mo × 6", fr: "Tête-aux-pieds Illimité · 249 $/mo × 6" }, href: "/laser-hair-removal#head-to-toe-unlimited", featured: true, locationNote: { en: "6-month all-inclusive plan", fr: "Forfait tout-inclus 6 mois" } },
    ],
  },
  {
    id: "facials-body",
    label: { en: "Éminence Facials & Body", fr: "Soins Éminence & Corps" },
    landingHref: "/treatments/eminence-facials",
    // Éminence facials + body rituals are delivered by the medical
    // aesthetician — Ottawa only, same anchor as Laser & Skin.
    defaultClinics: ["ottawa"],
    blurb: {
      en: "Éminence organic facials and body rituals — from Signature Glow to the Stone Crop Body Wrap.",
      fr: "Soins faciaux et rituels corps Éminence — du Signature Glow à l'enveloppement Stone Crop.",
    },
    image: "/eminence-stone-crop-photo-900.webp",
    items: [
      // Entry point — the full menu page
      { name: { en: "Éminence Facials & Body — Full Menu", fr: "Menu Complet Éminence · Faciaux & Corps" }, href: "/treatments/eminence-facials", featured: true, locationNote: { en: "Ottawa · with a Medical Aesthetician", fr: "Ottawa · avec une esthéticienne médicale" } },
      // CO₂Lift Carboxytherapy — Feb 2026 launch (brand-new educational page)
      { name: { en: "CO₂Lift Carboxytherapy — The No-Needle Lift", fr: "CO₂Lift Carboxythérapie — Le lift sans aiguille" }, href: "/co2lift-ottawa", featured: true, locationNote: { en: "NEW · 45 min · from $145", fr: "NOUVEAU · 45 min · dès 145 $" } },
      // Latisse® lash serum — Feb 2026 launch
      { name: { en: "Latisse® Eyelash Serum — Prescription Lash Growth", fr: "Latisse® — Croissance des cils sur ordonnance" }, href: "/latisse-ottawa", featured: true, locationNote: { en: "NEW · Rx · from $149", fr: "NOUVEAU · Rx · dès 149 $" } },
      // Facials — deep-links to each card on the Éminence page (Nov 2 2026 pricing)
      { name: { en: "Éminence Express Glow Facial", fr: "Facial Express Glow Éminence" }, href: "/treatments/eminence-facials#eminence-facial-express-glow", locationNote: { en: "30 min · $69", fr: "30 min · 69 $" } },
      { name: { en: "Éminence Signature Organic Facial", fr: "Facial Signature Éminence" }, href: "/treatments/eminence-facials#eminence-facial-signature-organic", featured: true, locationNote: { en: "Best place to start · 60 min · $119", fr: "Le meilleur point de départ · 60 min · 119 $" } },
      { name: { en: "Éminence Ultimate Organic Facial + LED", fr: "Facial Ultime Éminence + DEL" }, href: "/treatments/eminence-facials#eminence-facial-ultimate-organic", featured: true, locationNote: { en: "15 min LED included · 75 min · $159", fr: "15 min de DEL incluses · 75 min · 159 $" } },
      { name: { en: "Hormonal Imbalance Facial", fr: "Facial Déséquilibre Hormonal" }, href: "/treatments/eminence-facials#eminence-facial-eight-greens-phyto-facial", locationNote: { en: "60 min · $129", fr: "60 min · 129 $" } },
      // Body rituals — the new menu
      { name: { en: "Stone Crop Head-to-Toe Body Ritual", fr: "Renouveau Corps Stone Crop" }, href: "/treatments/eminence-facials#eminence-body-stone-crop-restorative-body", featured: true, locationNote: { en: "Signature · 75 min · $199", fr: "Signature · 75 min · 199 $" } },
      { name: { en: "Mango Enzyme Body Renewal", fr: "Renouveau Corps Enzyme Mangue" }, href: "/treatments/eminence-facials#eminence-body-mango-enzyme-body-renewal", locationNote: { en: "75 min · $199", fr: "75 min · 199 $" } },
      { name: { en: "Chocolate Truffle Age-Defying Body Wrap", fr: "Enveloppement Truffe Chocolat Anti-Âge" }, href: "/treatments/eminence-facials#eminence-body-chocolate-truffle-age-defying", locationNote: { en: "75 min · $219", fr: "75 min · 219 $" } },
      { name: { en: "Herbal Cellulite Firming Body Treatment", fr: "Traitement Cellulite Fermeté Aux Herbes" }, href: "/treatments/eminence-facials#eminence-body-herbal-cellulite-firming", locationNote: { en: "90 min · $229", fr: "90 min · 229 $" } },
      // Eight Greens Detox Body Ritual REMOVED (Sept 2026 · Karine ask).
      // Éminence has not authorised an Eight Greens body protocol — the
      // Eight Greens system is a FACIAL only (adult breakouts / oiliness
      // / dullness / loss of firmness — the Éminence-published positioning).
      // The "Hormonal Balance" naming was retired Sept 6 2026 because
      // cosmetics do not alter hormones; the facial is now marketed as
      // the "Éminence Eight Greens Phyto Facial" — a stimulating botanical
      // treatment built around the iconic Eight Greens collection with
      // the Hot / Not Hot Phyto Masque as the signature moment.
      // Do NOT re-add it as a body ritual.
    ],
  },
  {
    id: "vip",
    label: { en: "VIP & Memberships", fr: "VIP & Adhésions" },
    landingHref: "/treatments/vip-memberships",
    // Loyalty / membership / gift-card products are system-wide,
    // honoured at every clinic the member walks into.
    defaultClinics: ["ottawa", "kanata", "cp"],
    blurb: {
      en: "Crown Reserve, Aesthetic Wallet, memberships, gift cards, financing — the loyalty side.",
      fr: "Crown Reserve, Portefeuille Esthétique, abonnements, cartes-cadeaux, financement.",
    },
    image: "/img/treatments/vip.webp",
    items: [
      { name: { en: "Crown Reserve", fr: "Crown Reserve" }, href: "/crown-reserve", featured: true },
      { name: { en: "Aesthetic Wallet", fr: "Portefeuille Esthétique" }, href: "/crown-reserve-hub" },
      { name: { en: "Refer a Friend ($50)", fr: "Référez un Ami (50 $)" }, href: "/refer-a-friend" },
      { name: { en: "Gift Cards", fr: "Cartes-Cadeaux" }, href: "/gift-cards" },
      { name: { en: "Financing (Afterpay)", fr: "Financement (Afterpay)" }, href: "/afterpay" },
    ],
  },
];

export const CONCERNS = [
  {
    slug: "lines-wrinkles",
    label: { en: "Lines & Wrinkles", fr: "Rides & Ridules" },
    headline: {
      en: "Smooth lines without losing what makes you, you.",
      fr: "Atténuer les rides sans perdre votre expression.",
    },
    description: {
      en: "Forehead lines, crow's feet, and the 11's are the three areas where movement creates the first visible signs of ageing. Karine's micro-dose Botox technique softens them while keeping your face natural and expressive.",
      fr: "Le front, les pattes d'oie et les 11's sont les trois zones qui marquent en premier. La technique micro-dose de Karine adoucit sans figer.",
    },
    image: "/img/concerns/lines-wrinkles.webp",
    recommended: [
      { name: { en: "Botox — The Big 3", fr: "Botox — Les 3 Grandes Zones" }, price: "$499", href: "/botox-ottawa", best_for: "Forehead • Crow's Feet • 11's" },
      { name: { en: "Full Upper Face Package", fr: "Forfait Visage Supérieur" }, price: "$599", href: "/botox-ottawa", best_for: "7 areas + brow lift" },
      { name: { en: "Microneedling + PRP", fr: "Microneedling + PRP" }, price: "From $699", href: "/medical-microneedling", best_for: "Fine lines & texture" },
      { name: { en: "Skin Boosters (Baby Glow)", fr: "Skin Boosters" }, price: "From $450", href: "/skin-boosters", best_for: "Crepey skin & glow" },
    ],
  },
  {
    slug: "volume-contour",
    label: { en: "Volume & Contour", fr: "Volume & Contour" },
    headline: {
      en: "Restore the contour that gravity quietly took away.",
      fr: "Restaurer le contour que la gravité a discrètement effacé.",
    },
    description: {
      en: "Cheeks deflate. Jawlines soften. Chins recede. Strategic filler and Sculptra bio-stimulation rebuild lost structure — no overfilled face, just your bone structure restored.",
      fr: "Les joues se creusent, la mâchoire s'adoucit, le menton recule. Filler stratégique et Sculptra restaurent la structure perdue.",
    },
    image: "/img/concerns/volume-contour.webp",
    recommended: [
      { name: { en: "Dermal Fillers", fr: "Fillers Dermiques" }, price: "From $499", href: "/dermal-fillers", best_for: "Cheeks • Jawline • Chin" },
      { name: { en: "Sculptra Bio-Stimulator", fr: "Sculptra Bio-Stimulateur" }, price: "From $799", href: "/sculptra", best_for: "Long-term collagen rebuild" },
      { name: { en: "2 Syringes + FREE Botox", fr: "2 Seringues + Tox GRATUIT" }, price: "$950", href: "/dermal-fillers", best_for: "Full face refresh" },
    ],
  },
  {
    slug: "lip-enhancement",
    label: { en: "Lip Enhancement", fr: "Augmentation des Lèvres" },
    headline: {
      en: "Beautiful lips that still look like yours.",
      fr: "De belles lèvres qui restent les vôtres.",
    },
    description: {
      en: "From a subtle 1/2 syringe hydration boost to a full luscious shape, Karine tailors lip treatments to your natural anatomy — never duck-lipped, always proportioned.",
      fr: "D'une hydratation subtile (1/2 seringue) à une forme pleine et naturelle, Karine adapte les lèvres à votre anatomie.",
    },
    image: "/img/concerns/lip-enhancement.webp",
    recommended: [
      { name: { en: "Luscious Lip — Full Syringe", fr: "Lèvres Luxueuses — Seringue Complète" }, price: "$499", href: "/lip-filler-ottawa", best_for: "Volume + shape" },
      { name: { en: "Radiant Lip — 1/2 Syringe", fr: "Lèvres Radiantes — 1/2 Seringue" }, price: "$299", href: "/lip-filler-ottawa", best_for: "Subtle hydration" },
      { name: { en: "Lip Flip (Botox)", fr: "Lip Flip (Botox)" }, price: "$125", href: "/botox-ottawa", best_for: "No filler needed" },
      { name: { en: "Smokers Lines Combo", fr: "Combo Lignes de Fumeur" }, price: "$899", href: "/dermal-fillers", best_for: "Filler + Botox" },
    ],
  },
  {
    slug: "pigmentation-acne",
    label: { en: "Pigmentation & Acne", fr: "Pigmentation & Acné" },
    headline: {
      en: "Even-toned, clear, photo-ready skin.",
      fr: "Un teint uniforme, clair et prêt en photo.",
    },
    description: {
      en: "Sun spots, post-acne marks, melasma and active breakouts each need their own approach. Microneedling, PRP, Hydrafacials and targeted lasers work in combination.",
      fr: "Taches solaires, cicatrices d'acné, mélasma, boutons actifs — chacun mérite une approche différente.",
    },
    image: "/img/concerns/pigmentation.webp",
    recommended: [
      { name: { en: "Crown Platinum Hydrafacial", fr: "Hydrafacial Platinum" }, price: "$269", href: "/hydrafacial", best_for: "Brightening + clarity" },
      { name: { en: "Microneedling + Exosomes", fr: "Microneedling + Exosomes" }, price: "From $899", href: "/medical-microneedling", best_for: "Texture + pigment" },
      { name: { en: "Skin Boosters Glow Program", fr: "Programme Glow Boosters" }, price: "$1,799", href: "/skin-boosters", best_for: "Glass-skin tone" },
      { name: { en: "Dermaplaning", fr: "Dermaplaning" }, price: "$110", href: "/laser-and-skin", best_for: "Maintenance" },
    ],
  },
  {
    slug: "hair-removal",
    label: { en: "Hair Removal", fr: "Épilation" },
    headline: {
      en: "Permanent results from medical-grade laser.",
      fr: "Résultats permanents avec laser médical." ,
    },
    description: {
      en: "Brazilian, underarms, full body — our Health-Canada-approved diode laser works on all skin tones and most hair colours. Stack The House Package (Buy any 5 → the 6th is FREE at equal or lesser value) across any mix of areas, or go unlimited with the Head-to-Toe plan.",
      fr: "Brésilien, aisselles, corps complet — laser diode médical pour tous types de peau. Combinez la Promo Maison (Achetez 5 → la 6ᵉ GRATUITE, valeur égale ou inférieure) ou passez à l'illimité avec le forfait Tête-aux-pieds.",
    },
    image: "/img/concerns/hair-removal.webp",
    recommended: [
      { name: { en: "The House Package · Buy 5 → 6th FREE", fr: "Promo Maison · Achetez 5 → la 6ᵉ GRATUITE" }, price: "Equal or lesser value", href: "/laser-hair-removal", best_for: "Best per-area value" },
      { name: { en: "Brazilian Laser", fr: "Brésilien" }, price: "$55", href: "/laser-hair-removal", best_for: "Single area" },
      { name: { en: "Underarms Laser", fr: "Aisselles" }, price: "$30", href: "/laser-hair-removal", best_for: "Quick & affordable" },
      { name: { en: "Head-to-Toe Unlimited · $249/mo × 6", fr: "Tête-aux-pieds Illimité · 249 $/mo × 6" }, price: "$249/mo", href: "/laser-hair-removal", best_for: "Every area, every month" },
    ],
  },
  {
    slug: "skin-quality",
    label: { en: "Skin Quality & Glow", fr: "Qualité de Peau & Éclat" },
    headline: {
      en: "The glass-skin glow, without filters.",
      fr: "L'éclat \"glass skin\", sans filtres.",
    },
    description: {
      en: "Crepey texture, dullness, and dehydration aren't fixed by serums alone. Skin Boosters with Redensity 1, Hydrafacials and Microneedling all work at the dermal level for visible, lasting glow.",
      fr: "Texture mate, déshydratation, peau crépue : il faut traiter au niveau dermique.",
    },
    image: "/img/concerns/skin-quality.webp",
    recommended: [
      { name: { en: "The Baby Glow Infusion", fr: "L'Infusion Baby Glow" }, price: "$699", href: "/skin-boosters", best_for: "Entry point" },
      { name: { en: "Glass Skin Program (x3)", fr: "Programme Glass Skin (x3)" }, price: "$1,799", href: "/skin-boosters", best_for: "Most dramatic" },
      { name: { en: "Crown Deluxe Hydrafacial", fr: "Hydrafacial Crown Deluxe" }, price: "$199", href: "/hydrafacial", best_for: "Monthly maintenance" },
      { name: { en: "PRP Facial Rejuvenation", fr: "Rajeunissement PRP" }, price: "From $699", href: "/medical-microneedling", best_for: "Long-term collagen" },
    ],
  },
];
